import React from 'react'
import lodash from 'lodash'
import * as echarts from 'echarts'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { chartValueLabelStyle, classToFComponent } from 'src/utils/views'
import { EChartViewClass } from './helpers/base-echart'

import { ChartFontSize, ChartPalette, axisLabelStyle, seriesColor, tooltipStyle } from './helpers/tcm_chart_theme'
import { formatNumberToCompactShortForm } from './helpers/tcm_view_utils'
import { ChartEmpresasAtivasPorUfScope } from '../tcm_scopes'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-View-EmpresasAtivasPorUF')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto'
    }
})

type ChartEmpresasAtivasPorUFProps = {
    className?: string
    style?: React.CSSProperties
    scope: ChartEmpresasAtivasPorUfScope
}

class ChartEmpresasAtivasPorUFViewClass extends EChartViewClass<ChartEmpresasAtivasPorUFProps> {
    // :: Fields
    scope!: ChartEmpresasAtivasPorUfScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartEmpresasAtivasPorUFProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartEmpresasAtivasPorUFProps): React.JSX.Element {
        return (
            <TcmCard caption={scope.caption} className={className} style={style}>
                {super.render({ className: this.classes.view, scope })}
            </TcmCard>
        )
    }

    protected override onBindChartEvents(chart: echarts.ECharts) {
        chart.off('click', this.emitClickOnSeries)
        chart.on('click', this.emitClickOnSeries)
    }

    protected override onComputeSize(opts: echarts.ResizeOpts) {
        if (this.scope.dataset.length > 0) {
            if (this.scope.dataset.length > 10) {
                opts.height = Math.max(this.scope.dataset.length * 30, opts.height as number)
            }
        }
    }

    emitClickOnSeries = (evt: echarts.ECElementEvent) => {
        type DataItem = { id: string; value: number }
        let data = evt.data as DataItem
        if (!data && evt.dataIndex >= 0) {
            const serie = (this.chartData.series! as echarts.SeriesOption[])[0]
            if (serie && lodash.isArray(serie.data)) {
                const dataArray = serie.data as DataItem[]
                data = dataArray[evt.dataIndex]
            }
        }

        if (data) {
            this.scope.onItemClicked(data.id).catch(LOG.caught)
        }
    }
}

export default classToFComponent(ChartEmpresasAtivasPorUFViewClass, React)

function buildChartData(scope: ChartEmpresasAtivasPorUfScope): echarts.EChartsOption {
    const yAxisData: {
        value: string
        textStyle: {
            color: string
            fontSize: number
        }
    }[] = []

    const xAxisData: {
        id: string
        value: number
        itemStyle: {
            color: string
        }
    }[] = []

    for (const row of scope.dataset) {
        yAxisData.push({
            value: row.codigo ?? '',
            textStyle: {
                color: row.selected ? ChartPalette.selected : ChartPalette.label,
                fontSize: ChartFontSize.axisDense
            }
        })

        xAxisData.push({
            id: row.codigo,
            value: row.quantidade,
            itemStyle: {
                color: seriesColor(row.selected)
            }
        })
    }

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            ...tooltipStyle
        },
        grid: { containLabel: true, top: 0, left: 10, bottom: 5 },
        xAxis: {
            type: 'value',
            boundaryGap: [0, 0.01],
            axisLabel: {
                show: false
            },
            splitLine: { show: false }
        },
        yAxis: {
            type: 'category',
            axisLabel: {
                ...axisLabelStyle,
                fontSize: ChartFontSize.axisDense,
                width: 170
            },
            axisTick: { show: false },
            axisLine: { show: false },
            data: yAxisData,
            triggerEvent: true
        },
        series: [
            {
                type: 'bar',
                itemStyle: { borderRadius: [0, 3, 3, 0] },
                label: {
                    show: true,
                    align: 'left',
                    position: 'right',
                    color: chartValueLabelStyle.color,
                    fontSize: ChartFontSize.value,
                    fontWeight: 700,
                    formatter: (params) => {
                        return formatNumberToCompactShortForm(params.value as number)
                    }
                },
                data: xAxisData
            }
        ]
    }
}
